import axios from 'common/requester';
import { useContext, useEffect } from 'react';
import _ from 'lodash';
import usePmSensorAuthId from 'components/hooks/PM/usePmSensorAuthId';
import useRestoreFromStorage from 'common/hooks/storage/useRestoreFromStorage';
import { LINB_API } from 'common/constants/api-urls';
import { resourceAllocationTypesEnum } from '@linearb/linjs-common';
import { PERIODS } from 'components/PeriodPicker/periods/constants';
import useResourceAllocationService from 'common/services/resource-allocation.service.versions';
import { getFilterSets } from '../components/ProjectsFiltersBar/FilterSet/filter-set.service';
import { useIsSaveFilterSetEnabled } from '../components/ProjectsFiltersBar/FilterSet/useIsSaveFilterSetEnabled';
import useQueryParams from 'components/hooks/useQueryParams';
import { FILTERS_PROVIDER_QS_KEYS } from '../contexts';
import { useHistory } from 'react-router';
import AppRouter from 'common/AppRouter';
import useExtraPayload from './useExtraPayload';
import useContextActions from '../useContextActions';

const useInitialization = ({ Context, withInitialFilters }) => {
  const { state } = useContext(Context);
  const history = useHistory();
  const raService = useResourceAllocationService();
  const isSaveFilterSetEnabled = useIsSaveFilterSetEnabled();
  const pmSensorAuthId = usePmSensorAuthId();
  const { getQueryParam } = useQueryParams();
  const extraPayload = useExtraPayload(Context);
  const contextActions = useContextActions({ Context });

  const { storageKeys } = Context;
  const filtersFromStorage = useRestoreFromStorage({
    key: storageKeys.filters,
    defaultValue: {}
  });

  const periodFromStorage = useRestoreFromStorage({
    key: storageKeys.period,
    defaultValue: null
  });
  const selectedfilterSetIdFromStorage = useRestoreFromStorage({
    key: storageKeys.selectedFilterSetId,
    defaultValue: null
  });
  const Period = {
    ...periodFromStorage,
    ...(PERIODS[periodFromStorage?.periodKey] || PERIODS.PAST_30_DAYS)
  };

  const areFiltersFromStorageExists = _.size(filtersFromStorage) > 0;

  const shouldInitFilters = withInitialFilters && !areFiltersFromStorageExists;

  const getPdtEntities = async () => {
    try {
      return await axios.get(
        LINB_API.PM_SERVICE.PM_ENTITIES.PDT_PROJECT_FILTERS
      );
    } catch (e) {
      return [];
    }
  };

  const fetchFilterSets = async () => {
    try {
      const response = await getFilterSets();
      const items = response?.items || [];
      return _.keyBy(items, 'id');
    } catch (e) {
      return {};
    }
  };

  const getTopProjectsAsFilters = async () => {
    try {
      const res = await raService.getAggregatedTable({
        ...state.period,
        type: resourceAllocationTypesEnum.PROJECT,
        sensor_authorization_id: pmSensorAuthId,
        limit: 3,
        offset: 0,
        sort_by: 'effort_p',
        sort_dir: 'desc'
      });
      const projects = (res.data || [])
        .map(project => ({
          id: project.id,
          title: project.name
        }))
        .filter(project => !!project.id && !!project.title);
      return projects || [];
    } catch (e) {
      return [];
    }
  };

  const init = async () => {
    try {
      contextActions.reset();

      const pdtEntities = await getPdtEntities();
      const topProjectsFilters = shouldInitFilters
        ? await getTopProjectsAsFilters()
        : [];
      const filterSets = isSaveFilterSetEnabled ? await fetchFilterSets() : {};
      const filterSetIdFromQs = getQueryParam(
        FILTERS_PROVIDER_QS_KEYS.FILTER_SET_ID
      );
      if (filterSetIdFromQs && !filterSets?.[filterSetIdFromQs]) {
        return history.replace(AppRouter.INVALID_FILTER_SET.path, {
          from: {
            moduleName: Context.moduleName,
            url: window.location.pathname
          }
        });
      }

      const selectedFilterSetId = Boolean(filterSets?.[filterSetIdFromQs])
        ? filterSetIdFromQs
        : selectedfilterSetIdFromStorage;

      const filterSetFilters = filterSets?.[selectedFilterSetId]?.filters;

      const filters = filterSetFilters ?? {
        ...filtersFromStorage,
        ...(shouldInitFilters && {
          projects: topProjectsFilters
        })
      };

      await contextActions.onInitFilters({
        period: Period,
        setPeriod: contextActions.onSelectPeriod,
        filters,
        pdtEntities,
        filterSets,
        selectedFilterSetId,
        extraPayload
      });
    } catch (e) {
      contextActions.onInitFilters();
    }
  };

  useEffect(() => {
    init();
  }, [filtersFromStorage]);
};

export default useInitialization;
